import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { SEARCH_DEBOUNCE_MS } from '@/utils/constants';
import { cn } from '@/utils/cn';

interface SearchResult {
  id: string;
  description: string;
  place_id: string;
}

interface SearchFieldProps {
  onPlaceSelect: (placeId: string) => void;
  onCurrentLocation: () => void;
  isLoadingLocation?: boolean;
  className?: string;
}

/**
 * Search field with Google Places autocomplete
 */
export function SearchField({
  onPlaceSelect,
  onCurrentLocation,
  isLoadingLocation = false,
  className,
}: SearchFieldProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Handle search query using AutocompleteSuggestion API (new recommended approach)
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    // Check if Google Maps API is loaded
    if (!window.google?.maps?.places?.AutocompleteSuggestion) {
      console.warn('[SearchField] Google Maps Places API not loaded');
      return;
    }

    setIsLoading(true);

    // Use the new AutocompleteSuggestion API (Promise-based)
    const request = {
      input: debouncedQuery,
      includedPrimaryTypes: ['geocode'], // Only return geographic locations
    };

    // Fetch autocomplete suggestions using the new API
    google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
      request
    )
      .then((response) => {
        setIsLoading(false);

        if (response.suggestions && response.suggestions.length > 0) {
          setResults(
            response.suggestions.map((suggestion) => ({
              id: suggestion.placePrediction?.placeId || '',
              description: suggestion.placePrediction?.text?.text || '',
              place_id: suggestion.placePrediction?.placeId || '',
            }))
          );
          setIsOpen(true);
          setSelectedIndex(-1);
        } else {
          // No results found
          setResults([]);
          setIsOpen(false);
        }
      })
      .catch((error) => {
        console.error(
          '[SearchField] Error fetching autocomplete suggestions:',
          error
        );
        setIsLoading(false);
        setResults([]);
        setIsOpen(false);
      });
  }, [debouncedQuery]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || results.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          const selectedResult = results[selectedIndex];
          if (selectedResult) {
            handleSelectResult(selectedResult);
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle result selection
  const handleSelectResult = (result: SearchResult) => {
    setQuery(result.description);
    setIsOpen(false);
    setSelectedIndex(-1);
    onPlaceSelect(result.place_id);
    inputRef.current?.blur();
  };

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (!e.target.value.trim()) {
      setIsOpen(false);
    }
  };

  // Clear search
  const handleClear = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  return (
    <div className={cn('relative', className)}>
      {/* Search Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true);
          }}
          placeholder={t('search.placeholder')}
          className="surface-float w-full h-12 pl-12 pr-20 rounded-full border-0 text-[15px] text-ink placeholder-muted focus:outline-none focus:ring-2 focus:ring-ink"
        />

        {/* Search Icon */}
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <svg
            className="w-5 h-5 text-muted"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        {/* Right Icons */}
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
          {/* Loading indicator OR Clear button */}
          {query && (
            <div className="p-2">
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-ink border-t-transparent rounded-full animate-spin" />
              ) : (
                <button
                  onClick={handleClear}
                  className="rounded-full hover:bg-ink/5 transition-colors"
                  aria-label={t('common.clear')}
                >
                  <svg
                    className="w-4 h-4 text-muted"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Current location button */}
          <button
            onClick={onCurrentLocation}
            disabled={isLoadingLocation}
            className="p-2 rounded-full hover:bg-ink/5 transition-colors disabled:opacity-50"
            aria-label={t('search.currentLocation')}
          >
            {isLoadingLocation ? (
              <div className="w-4 h-4 border-2 border-ink border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg
                className="w-4 h-4 text-ink"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Results Dropdown */}
      {isOpen && results.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-surface text-ink rounded-2xl shadow-monta-lg ring-1 ring-ink/10 overflow-hidden z-50"
        >
          <ul className="max-h-80 overflow-y-auto">
            {results.map((result, index) => (
              <li key={result.id}>
                <button
                  onClick={() => handleSelectResult(result)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={cn(
                    'w-full px-4 py-3 text-left text-sm transition-colors',
                    'hover:bg-ink/5',
                    selectedIndex === index && 'bg-ink/5'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <svg
                      className="w-5 h-5 text-muted flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span className="text-ink">{result.description}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
